import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/common/Button';
import ApplyJobModal from '../components/jobs/ApplyJobModal';
import AudioPlayer from '../components/jobs/AudioPlayer';
import JobApplicationsSection from '../components/jobs/JobApplicationsSection';
import JobDistanceMap from '../components/jobs/JobDistanceMap';
import TabbedServiceBanner from '../components/home/TabbedServiceBanner';
import { getSupabaseJob } from '../lib/supabase/jobs';
import { getJob as getFirebaseJob } from '../lib/firebase/jobs';
import type { Job } from '../types/jobs';

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showApplyModal, setShowApplyModal] = useState(false);

  useEffect(() => {
    async function fetchJob() {
      if (!id) return;
      
      try {
        // First try Supabase (new jobs), then fallback to Firebase (existing jobs)
        const supabaseResult = await getSupabaseJob(id);
        
        if (supabaseResult.job) {
          setJob(supabaseResult.job);
        } else {
          // If not found in Supabase, try Firebase
          const firebaseResult = await getFirebaseJob(id);
          if (firebaseResult.job) {
            setJob(firebaseResult.job);
          } else {
            throw new Error('Job not found');
          }
        }
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    }

    fetchJob();
  }, [id]);

  const handleBack = () => {
    navigate('/jobs');
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error || 'Job not found'}</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={handleBack}
          >
            Back to Jobs
          </Button>
        </div>
      </div>
    );
  }

  // Show Apply button if: user is not the job owner AND job is open
  // Always show for logged out users (they'll be prompted to log in)
  // Always show for logged in users who don't own the job
  const isJobOwner = user && user.id === job.farmerId;
  const canApply = !isJobOwner && job.status === 'open';

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 pb-24">
      <button
        onClick={handleBack}
        className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Jobs
      </button>

      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6 gap-4">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{job.title}</h1>
            <div className="mt-2 flex flex-wrap gap-2 sm:space-x-4">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 capitalize">
                {job.type}
              </span>
              <span className="inline-flex items-center text-gray-600 text-sm">
                <MapPin className="h-4 w-4 mr-1" />
                {job.location}
              </span>
            </div>
          </div>
          <div className="sm:text-right flex flex-col items-end gap-3">
            <div>
              <div className="text-xl sm:text-2xl font-bold text-green-600">
                {job.payRate === 'DOE' ? 'DOE' : `$${job.payRate}/hr`}
              </div>
              <div className="text-xs sm:text-sm text-gray-500">
                Posted {new Date(job.createdAt).toLocaleDateString()}
              </div>
            </div>
            {canApply && (
              <button
                onClick={() => setShowApplyModal(true)}
                className="cowboy-btn cowboy-btn-apply !hidden md:!inline-flex"
              >
                Apply Now
              </button>
            )}
            {job.status !== 'open' && (
              <div className="hidden sm:block bg-gray-100 text-gray-600 text-center py-2 px-4 rounded-md text-sm">
                Position Filled
              </div>
            )}
          </div>
        </div>

        {job.audioMessageUrl && (
          <div className="mb-6">
            <AudioPlayer audioUrl={job.audioMessageUrl} showLabel />
          </div>
        )}

        <div className="prose max-w-none mb-8">
          <p className="text-sm sm:text-base text-gray-700 whitespace-pre-line leading-relaxed">{job.description}</p>
        </div>

        {job.requirements.length > 0 && (
          <div className="mb-8">
            <h2 className="text-base sm:text-lg font-semibold mb-4">Requirements</h2>
            <ul className="list-disc list-inside space-y-2 text-sm sm:text-base text-gray-700">
              {job.requirements.map((req, index) => (
                <li key={index}>{req}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Show applications section only to job owner */}
        {user?.id === job.farmerId && (
          <div className="mt-8 border-t border-gray-200 pt-6">
            <JobApplicationsSection jobId={job.id} />
          </div>
        )}

        {/* Distance and map view */}
        <JobDistanceMap jobLocation={job.location} jobTitle={job.title} />
      </div>

      <div className="mt-8">
        <TabbedServiceBanner />
      </div>

      {showApplyModal && (
        <ApplyJobModal
          job={job}
          onClose={() => setShowApplyModal(false)}
        />
      )}

      {/* Apply Button - Sticky on mobile only */}
      {canApply && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-50 sm:hidden">
          <button
            onClick={() => setShowApplyModal(true)}
            className="cowboy-btn cowboy-btn-apply w-full"
          >
            Apply Now
          </button>
        </div>
      )}

      {/* Position Filled - Mobile view */}
      {job.status !== 'open' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-50 sm:hidden">
          <div className="bg-gray-100 text-gray-600 text-center py-3 rounded-md">
            Position Filled
          </div>
        </div>
      )}
    </div>
  );
}